import { EcomodClass } from "../api/models/ecomodClass.models";
import { PackageRow } from "../api/models/packageRow.models";
import { Price } from "../api/models/prices.models";
import { Report, ReportRow, ReportType } from "../api/models/report.models";
import {
  RowValues,
  RowValuesWithEcomodClass,
} from "../api/models/rowValues.models";
import { SupClass } from "../api/models/supClass.models";
import { ExcelData } from "../contexts/excelContext";
import { Locale } from "../types/ui-types";
import { getClassLabelFromAkeneoObject, getReusableClass } from "./akeneoUtils";
import { getRowPrice, PriceType } from "./priceUtils";

export enum ReusableClass {
  NONE = 0,
  FIRST_USE = 1,
  REUSABLE = 2,
}

export enum CustomerType {
  BUSINESS = "Yritys",
  CONSUMER = "Kuluttaja",
}

export interface WizardSelection {
  id?: number;
  mainCategory?: string;
  customerType?: string;
  ecomodCategory?: string;
  isSup: boolean;
  supClass?: string;
  amount: number;
  reusableClass: ReusableClass;
  packagedAbroad?: boolean;
  secMaterial?: string; // secondary material type , same as ecomod type
  secValue?: number; // secondary material value
  secValueType?: 1 | 2; // 1 = kg, 2 = percentage
  isEmptyCup?: boolean;
}

export const isInteger = (str: string) => {
  return /^[0-9]+$/.test(str);
};

export const isEcomodRow = (row: ReportRow): boolean => {
  return !row.values.every((value) => {
    return isInteger(value.ecoModulationClass?.code as string);
  });
};

// Every row needs to be ecomod row for the whole report to be ecomod, no simple rows allowed
export const isEcomodReport = (rows: Array<ReportRow>): boolean => {
  return rows.every(isEcomodRow);
};

export const getRowTotalWeight = (
  row: ReportRow,
  skipReusable?: boolean,
  reportType?: ReportType,
): number => {
  const total = row.values.reduce((total, rowValue) => {
    const value = rowValue.value;
    const secValue = rowValue.secValue;
    if (skipReusable && rowValue.reusableClass === ReusableClass.REUSABLE) {
      return total;
    }

    if (reportType === ReportType.LIGHT || reportType === ReportType.DETAILED) {
      // For rinki reports, include secondary material weight in total weight
      return (
        total +
        (value ? parseFloat(value.toString()) : 0) +
        (secValue ? parseFloat(secValue.toString()) : 0)
      );
    }

    // For product related reports, secondary material weight is included in product weight already
    return total + (value ? parseFloat(value.toString()) : 0);
  }, 0);
  return total;
};

export const getReportTotalWeight = (
  rows: Array<ReportRow>,
  skipReusable?: boolean,
  reportType?: ReportType,
): number => {
  return rows.reduce((total, row) => {
    return (
      total + getRowTotalWeight(row, skipReusable, reportType) * row.amount
    );
  }, 0);
};

export const getExcelProductsWithSameMaterial = (
  data: ExcelData[],
  key: string,
): {
  name: string;
  ean: string;
  amount: number;
  active: boolean;
}[] => {
  const filteredData = data
    .filter((item) => item[key] !== undefined && item[key] !== "0")
    .map((item) => ({
      name: item.productName,
      ean: item.ean,
      amount: parseFloat(item[key]),
      active: true,
    }));

  return filteredData;
};

export const getRowProductsWithSameMaterial = (
  data: ReportRow[],
  key: string,
): {
  name: string;
  ean: string;
  amount: number;
  active: boolean;
  weight: number;
}[] => {
  const filteredData = data
    .filter(
      (item) =>
        item[key] !== undefined &&
        item[key] !== "0" &&
        item[key] !== 0 &&
        item[key] !== null,
    )
    .map((item) => ({
      name: item.name,
      ean: item.ean,
      amount: item.amount,
      active: item.active,
      weight: parseFloat(item[key]),
    }));

  return filteredData;
};

export const getMaterialWeightFromSubRows = (
  rows: {
    key: string;
    label: string;
    combinedWeight: number;
  }[],
): number => {
  return rows.reduce((total, row) => total + row.combinedWeight, 0);
};

export const containsSupMaterials = (value: RowValuesWithEcomodClass) => {
  return !!value.supClass;
};

export const getCombinedWeight = (
  ecomodCode: string,
  reportData: Report,
  type: "business" | "consumer",
): { totalWeight: number; recycledWeight: number } => {
  let totalWeight = 0;
  let recycledMaterialsWeight = 0;

  const isRinkiReport =
    reportData.reportType === ReportType.LIGHT ||
    reportData.reportType === ReportType.DETAILED;

  reportData?.rows.forEach((row: ReportRow) => {
    // Skip inactive rows - means user is not responsible to report or pay for them
    if (row.active === false) {
      return;
    }

    const matchingRowValues = row.values.filter((value) => {
      if (type === "business" && !value.businessUse) {
        return false;
      }
      if (type === "consumer" && value.businessUse) {
        return false;
      }

      if (isRinkiReport && !!value.supClass) {
        return false;
      }

      return (
        value.ecoModulationClass?.code === ecomodCode ||
        value.ecoModulationClass?.code?.startsWith(ecomodCode)
      );
    });

    const matchingRowWeight = matchingRowValues.reduce((acc, value) => {
      if (value.reusableClass === ReusableClass.REUSABLE) {
        recycledMaterialsWeight += value.value * row.amount;

        // Also add secondary material to recycled weight if defined but only for Rinki reports
        // For product related reports, secondary materials are included in the total product weight
        if (
          isRinkiReport &&
          value.secMaterial &&
          value.secValue &&
          value.secValue > 0
        ) {
          recycledMaterialsWeight += value.secValue * row.amount;
        }
      }

      // if secMaterials is defined, we need to include that in the weight calculation for Rinki reports
      // For product related reports, secondary materials are included in the total product weight
      if (
        isRinkiReport &&
        value.secMaterial &&
        value.secValue &&
        value.secValue > 0
      ) {
        return acc + (value.secValue + value.value) * row.amount;
      }

      return acc + value.value * row.amount;
    }, 0);
    totalWeight += matchingRowWeight;
  });

  return { totalWeight: totalWeight, recycledWeight: recycledMaterialsWeight };
};

export const getCombinedSupClassWeight = (
  ecomodClassCode: string | null,
  reportData: Report,
  isRinkiReport?: boolean,
): number => {
  let totalWeight = 0;
  reportData?.rows.forEach((row: ReportRow) => {
    // Skip inactive rows - means user is not responsible to report or pay for them
    if (row.active === false) {
      return;
    }

    const matchingRowValues = row.values.filter((value) => {
      // Find matching values but only for rinki reports for other materials
      if (ecomodClassCode === null) {
        return isRinkiReport && !value.ecoModulationClass;
      }

      // For specific ecomod class, find sup values linked to that ecomod class
      return (
        value.ecoModulationClass?.code?.startsWith(ecomodClassCode) &&
        (isRinkiReport
          ? true
          : value.ecoModulationClass?.code !== ecomodClassCode)
      );
    });
    const matchingRowWeight = matchingRowValues.reduce(
      (acc, value) =>
        containsSupMaterials(value) ? acc + value.value * row.amount : acc,
      0,
    );
    totalWeight += matchingRowWeight;
  });

  return totalWeight;
};

export const getEcomodMaterials = (
  ecomodCode: string,
  ecomodClasses: EcomodClass[],
  reportData: Report,
  type: "business" | "consumer",
  locale: Locale,
  pricesData?: Price[],
): {
  key: string;
  label: string;
  combinedWeight: number;
  recycledWeight: number;
  price?: number;
}[] => {
  const ecomodMaterials: {
    key: string;
    label: string;
    combinedWeight: number;
    recycledWeight: number;
    price?: number;
  }[] = [];

  const subMaterials = ecomodClasses?.filter(
    (ecomodClass) =>
      ecomodClass.code?.startsWith(ecomodCode) &&
      ecomodClass.code !== ecomodCode,
  );

  subMaterials?.forEach((ecomodClass) => {
    const { totalWeight, recycledWeight } = getCombinedWeight(
      ecomodClass.code as string,
      reportData,
      type,
    );

    // Remove recycled weight from total weight for price calculation
    const nonRecyledWeight =
      recycledWeight > 0 && totalWeight > 0
        ? totalWeight - recycledWeight
        : totalWeight;

    const ecomodId = ecomodClasses.find((e) => e.code === ecomodClass.code)?.id;

    const subMaterial: {
      key: string;
      label: string;
      combinedWeight: number;
      recycledWeight: number;
      price?: number;
    } = {
      key: ecomodClass.code as string,
      label: getClassLabelFromAkeneoObject(locale, ecomodClass) as string,
      combinedWeight: totalWeight,
      recycledWeight: recycledWeight,
      price: getRowPrice(
        nonRecyledWeight,
        pricesData,
        ecomodId,
        undefined,
        type === "business" ? PriceType.ForBusiness : PriceType.ForConsumer,
      ),
    };

    ecomodMaterials.push(subMaterial);
  });

  return ecomodMaterials;
};

export const getSupMaterials = (
  ecomodMainCategoryCode: string | null,
  supClasses: SupClass[],
  reportData: Report,
  locale: Locale,
  pricesData?: Price[],
): {
  key: string;
  label: string;
  combinedWeight: number;
  recycledWeight: number;
  price?: number;
}[] => {
  const supMaterials: {
    key: string;
    label: string;
    combinedWeight: number;
    recycledWeight: number;
    price?: number;
  }[] = [];

  const isRinkiReport =
    reportData.reportType === ReportType.LIGHT ||
    reportData.reportType === ReportType.DETAILED;

  supClasses?.forEach((supClass) => {
    const supMaterialCombinedWeight = reportData?.rows.reduce((total, row) => {
      const matchingRowValues = row.values.filter((value) => {
        // For rinki reports, ecomodClassCode might be null, so we need to handle that case
        if (ecomodMainCategoryCode === null && isRinkiReport) {
          return (
            !value.ecoModulationClass && value.supClass?.code === supClass.code
          );
        }

        return (
          ecomodMainCategoryCode &&
          value.supClass?.code === supClass.code &&
          value.ecoModulationClass?.code !== supClass.code &&
          value.ecoModulationClass?.code?.startsWith(ecomodMainCategoryCode)
        );
      });
      const matchingRowWeight = matchingRowValues.reduce(
        (acc, value) => acc + value.value * row.amount,
        0,
      );
      return total + matchingRowWeight;
    }, 0);

    const supItem = supClasses.find((item) => item.code === supClass.code);

    const price = getRowPrice(
      supMaterialCombinedWeight,
      pricesData,
      undefined,
      supItem?.id,
      PriceType.PerKg,
    );

    const supMaterial: {
      key: string;
      label: string;
      combinedWeight: number;
      recycledWeight: number;
      price?: number;
    } = {
      key: supClass.code as string,
      label: getClassLabelFromAkeneoObject(locale, supClass) as string,
      combinedWeight: supMaterialCombinedWeight,
      price: price,
      recycledWeight: 0,
    };
    supMaterials.push(supMaterial);
  });

  return supMaterials;
};

export const getDefaultValuesFromReportRow = (
  reportRowValues: RowValuesWithEcomodClass[],
  ecomodClasses: EcomodClass[],
  supClasses: SupClass[],
): WizardSelection[] => {
  const defaultValues: WizardSelection[] = [];
  reportRowValues.forEach((value) => {
    const ecomodClass = ecomodClasses.find(
      (ecomodClass) => ecomodClass.id === value.ecoModulationClass?.id,
    );

    const subClass = supClasses?.find(
      (subClass) => subClass.id === value.supClass?.id,
    );

    if (!ecomodClass?.code) {
      console.error(`Ecomod code not found`);
      return;
    }

    let mainCategory;
    let ecomodCategory;

    if (isInteger(ecomodClass?.code as string)) {
      mainCategory = ecomodClass?.code;
      ecomodCategory = undefined;
    } else {
      mainCategory = ecomodClass?.code.charAt(0);
      ecomodCategory = ecomodClass?.code;
    }

    defaultValues.push({
      id: value.id,
      mainCategory: mainCategory,
      ecomodCategory: ecomodCategory,
      supClass: subClass?.code,
      customerType: value.businessUse
        ? CustomerType.BUSINESS
        : CustomerType.CONSUMER,
      reusableClass: value.reusableClass || 0,
      amount: value.value,
      isSup: !!value.supClass,
      packagedAbroad: value.packagedAbroad || false,
      secMaterial: value.secMaterial?.code || undefined,
      secValue: value.secValue || 0,
      secValueType: value.secValueType,
      isEmptyCup: value.isEmptyCup || false,
    });
  });
  return defaultValues;
};

export const getDefaultValuesFromPackageValues = (
  reportRowValues: RowValues[],
  ecomodClasses: EcomodClass[],
  supClasses: SupClass[],
): WizardSelection[] => {
  const defaultValues: WizardSelection[] = [];

  reportRowValues.forEach((value) => {
    const ecomodClass = ecomodClasses.find(
      (ecomodClass) => ecomodClass.code === value.ecomod_class,
    );

    const supClass = supClasses?.find(
      (subClass) => subClass.code === value.sup_class,
    );

    const secMaterial = ecomodClasses.find(
      (ecomodClass) => ecomodClass.code === value.secondary_material_type,
    );

    if (!ecomodClass?.code) {
      console.error(`Ecomod code not found`);
      return;
    }

    if (secMaterial && !secMaterial.code) {
      console.error(`Secondary material not found`);
      return;
    }

    let mainCategory;
    let ecomodCategory;

    if (isInteger(ecomodClass?.code as string)) {
      mainCategory = ecomodClass?.code;
      ecomodCategory = undefined;
    } else {
      mainCategory = ecomodClass?.code.charAt(0);
      ecomodCategory = ecomodClass?.code;
    }

    defaultValues.push({
      id: undefined,
      mainCategory: mainCategory,
      ecomodCategory: ecomodCategory,
      supClass: supClass?.code,
      customerType:
        value.business_use === "yes"
          ? CustomerType.BUSINESS
          : CustomerType.CONSUMER,
      reusableClass: getReusableClass(value.reusable_class) || 0,
      amount: value.value,
      isSup: !!supClass,
      secMaterial: secMaterial?.code || undefined,
      secValue: value.secondary_material_value || 0,
      secValueType: value.secondary_material_value_type === "kg" ? 1 : 2,
      isEmptyCup: value.empty_cup === "yes" ? true : false,
      packagedAbroad: value.packaged_abroad === "yes" ? true : false,
    });
  });
  return defaultValues;
};

export const convertAkeneoRowsToEcomodRows = (
  akeneoRows: RowValues[],
  ecomodClasses: EcomodClass[],
  supClasses: SupClass[],
): RowValuesWithEcomodClass[] => {
  return akeneoRows.map((row) => {
    const ecoModulationClass = ecomodClasses.find(
      (ecomodClass) => ecomodClass.code === row.ecomod_class,
    );
    const supClass = supClasses.find(
      (supClass) => supClass.code === row.sup_class,
    );

    const secMaterial = ecomodClasses.find(
      (ecomodClass) => ecomodClass.code === row.secondary_material_type,
    );

    const rowObject: RowValuesWithEcomodClass = {
      value: row.value,
      ecoModulationClass,
      supClass,
      secMaterial: secMaterial || null,
      secValue: row.secondary_material_value || 0,
      secValueType: row.secondary_material_value_type === "kg" ? 1 : 2, //
      businessUse: row.business_use === "yes" ? true : false,
      reusableClass: getReusableClass(row.reusable_class),
      packagedAbroad: row.packaged_abroad === "yes" ? true : false,
      isEmptyCup: row.empty_cup === "yes" ? true : false,
    };

    return rowObject;
  });
};

const getReusableClassString = (value?: number | null) => {
  switch (value) {
    case 0:
      return "none";
    case 1:
      return "first_use";
    case 2:
      return "reuse";
    default:
      return "none";
  }
};

export const convertEcomodRowsToAkeneoRows = (
  ecomodRows: RowValuesWithEcomodClass[],
): RowValues[] => {
  return ecomodRows.map((row) => {
    return {
      ecomod_class: row.ecoModulationClass?.code
        ? row.ecoModulationClass.code?.toString()
        : "none",
      sup_class: row.supClass?.code ? row.supClass.code : "none",
      business_use: row.businessUse ? "yes" : "no",
      reusable_class: getReusableClassString(row.reusableClass),
      packaged_abroad: row.packagedAbroad ? "yes" : "no",
      value: row.value,
      secondary_material_type: row.secMaterial?.code
        ? row.secMaterial.code.toString()
        : "none",
      secondary_material_value: row.secValue || 0,
      secondary_material_value_type: row.secValueType
        ? row.secValueType.toString()
        : "none",
      empty_cup: row.isEmptyCup ? "yes" : "no",
    };
  });
};

export interface ReportRowValueChange {
  label: string;
  oldWeight: number;
  newWeight: number;
}

export interface ReportRowComparison {
  hasChanges: boolean;
  nameChanged: boolean;
  oldName: string;
  newName: string;
  activeChanged: boolean;
  oldActive: boolean;
  newActive: boolean;
  totalWeightChanged: boolean;
  oldTotalWeight: number;
  newTotalWeight: number;
  valueChanges: ReportRowValueChange[];
}

const getValueWeight = (value?: RowValuesWithEcomodClass): number =>
  value ? (value.value || 0) + (value.secValue || 0) : 0;

// Round to avoid floating point comparison issues
const isSameWeight = (a: number, b: number): boolean =>
  Math.round(a * 100) === Math.round(b * 100);

export const compareReportRowWithFreshValues = (
  row: ReportRow,
  freshName: string,
  freshActive: boolean,
  freshValues: RowValuesWithEcomodClass[],
  locale: Locale,
): ReportRowComparison => {
  const nameChanged = row.name !== freshName;
  const activeChanged = row.active !== freshActive;

  const oldTotalWeight = row.values.reduce(
    (total, value) => total + getValueWeight(value),
    0,
  );
  const newTotalWeight = freshValues.reduce(
    (total, value) => total + getValueWeight(value),
    0,
  );

  const valueChanges: ReportRowValueChange[] = [];
  const maxLength = Math.max(row.values.length, freshValues.length);

  for (let i = 0; i < maxLength; i++) {
    const oldValue = row.values[i];
    const newValue = freshValues[i];
    const oldWeight = getValueWeight(oldValue);
    const newWeight = getValueWeight(newValue);

    if (!isSameWeight(oldWeight, newWeight)) {
      const label =
        getClassLabelFromAkeneoObject(
          locale,
          newValue?.ecoModulationClass || oldValue?.ecoModulationClass || {},
        ) || `#${i + 1}`;

      valueChanges.push({ label, oldWeight, newWeight });
    }
  }

  const totalWeightChanged = !isSameWeight(oldTotalWeight, newTotalWeight);

  const hasChanges =
    nameChanged ||
    activeChanged ||
    totalWeightChanged ||
    valueChanges.length > 0;

  return {
    hasChanges,
    nameChanged,
    oldName: row.name,
    newName: freshName,
    activeChanged,
    oldActive: row.active,
    newActive: freshActive,
    totalWeightChanged,
    oldTotalWeight,
    newTotalWeight,
    valueChanges,
  };
};

export const getTotalPrice = (
  price: number,
  pricesData: Price[],
  values: RowValuesWithEcomodClass[],
) => {
  if (!values || values.length === 0) {
    return price || 0;
  }

  let total = price || 0;

  values.forEach((value) => {
    const isBusiness = value.businessUse;
    const matchingPrice = pricesData?.find(
      (price) =>
        price.ecomodClassId ===
        (value.ecoModulationClass ? value.ecoModulationClass.id : -1),
    );

    if (matchingPrice) {
      const perKgPriceInfo = matchingPrice.priceInfo.find((info) =>
        isBusiness
          ? info.suffix === "ForBusiness"
          : info.suffix === "ForConsumer",
      );

      if (perKgPriceInfo) {
        total += perKgPriceInfo.amount * (value.value || 0);
      }
    }
  });

  return total;
};
